import React, { useEffect, useMemo } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import classNames from 'classnames';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { useFormStore, useRequest } from '@stores';

import { generateValidation } from '@components/dynamic-form/dynamic-form.validation';
import ErrorSummary from '@components/error-summary/ErrorSummary';
import { MarkdownRenderer } from '@components/markdown-renderer/MarkdownRenderer';
// import FileDownloadList from '@components/file-download-list/FileDownloadList';
import { getFormContentVisibility } from '@components/dynamic-form/helpers';

import Section from './Section';
import './dynamicForm.scss';

const isSectionVisible = (section, { methods }) => {
  const questions = section?.questions ?? [];
  if (!questions.length) return false;
  const formValues = methods.getValues();

  return questions.some((question) => {
    const parentThreads = question.parentThreads || [];

    return getFormContentVisibility({
      formValues,
      parentThreads,
    });
  });
};

const getHeaderVisibility = (sections, index, context) => {
  const nextNonHeader = sections.find((s, i) => i > index && !s.isHeader);
  return nextNonHeader ? isSectionVisible(nextNonHeader, context) : false;
};

const getFormHasQuestions = (formData) =>
  formData?.sections?.some((section) => section?.questions?.length > 0) ?? false;

const Form = ({
  allowAutoScroll = true,
  componentID,
  formData,
  isInformation,
  isReadOnly,
  onChange = () => {},
  onValidationChange = () => {},
  showErrorSummary = !isReadOnly,
  stepNo,
}) => {
  const formHasQuestions = useMemo(() => getFormHasQuestions(formData), [formData]);

  const { defaultValues, questionVisibility, setFullQuestionIds } = useFormStore();
  const { isSectionTouched, isSectionHidden } = useRequest();
  const isHidden = isSectionHidden(stepNo);

  //@TODO implement file download logic
  // const initialSectionDocuments = formData?.formDocuments?.filter((file) => file?.isInitialDisplayLocation);
  // const formSectionDocuments = formData?.formDocuments?.filter((file) => file?.isFullDisplayLocation);

  useEffect(() => {
    if (formData?.sections) {
      const fullQuestionIds = formData?.sections
        .map((section) => section.questions?.map((question) => question.questionId))
        .flat();
      setFullQuestionIds(fullQuestionIds);
    }
  }, [formData?.sections, setFullQuestionIds]);

  const validationSchema = useMemo(() => {
    if (isHidden) {
      return yup.object().shape({});
    } else {
      return yup.object().shape(generateValidation(formData?.sections, questionVisibility));
    }
  }, [formData?.sections, questionVisibility, isHidden]);

  const methods = useForm({
    defaultValues: defaultValues,
    resolver: yupResolver(validationSchema),
    mode: 'onBlur',
  });

  const {
    formState: { errors },
    trigger,
    reset,
    getValues,
  } = methods;
  const isValid = Object.keys(errors || {}).length === 0;
  const isTouched = isSectionTouched(stepNo);

  const visibilityContext = useMemo(
    () => ({
      methods,
      formData,
    }),
    [methods, formData]
  );

  const handleOnChange = ({ inputName, value, isEndConditionMet }) => {
    const formData = getValues();
    const updatedData = { ...formData, [inputName]: value };

    onChange(updatedData, isEndConditionMet);
  };

  useEffect(() => {
    onValidationChange?.(isValid);
  }, [isValid, onValidationChange]);

  useEffect(() => {
    reset(defaultValues, {
      keepErrors: true,
    });
  }, [defaultValues, reset]);

  useEffect(() => {
    isTouched && trigger();
  }, [trigger, isTouched, questionVisibility]); // TODO: just check which has changes

  useEffect(() => {
    if (!isInformation && isHidden) {
      reset({});
    } else if (!isInformation && !isHidden) {
      reset(getValues(), {
        resolver: yupResolver(validationSchema),
      });
      isTouched && trigger();
    }
  }, [validationSchema, isTouched, reset, trigger, getValues, isHidden, isInformation]);

  const containerStyles = classNames('dynamic-form-container', {
    'dynamic-no-questions': !formHasQuestions,
  });

  const introText = isInformation ? formData?.initialContent : formData?.regionHeader;
  const footerText = formData?.jpaRegionFooter;

  return (
    <FormProvider {...methods}>
      <div className={containerStyles}>
        {formHasQuestions && isTouched && showErrorSummary && <ErrorSummary defaultExpanded={true} errors={errors} />}
        <div className='dynamic-form'>
          {formData?.subtitle && <div className='dynamic-form-subtitle'>{formData?.subtitle}</div>}
          <h2 className='dynamic-form-title'>{formData?.formTitle}</h2>
          <div className={`dynamic-form-header ${!introText ? 'no-content' : ''}`}>
            <MarkdownRenderer text={introText} />
            {/* <FileDownloadList files={isInformation ? initialSectionDocuments : formSectionDocuments} /> */}
          </div>
          {formData?.sections?.map((section, index) => {
            const isHeaderVisible = getHeaderVisibility(formData?.sections, index, visibilityContext);

            if (section?.isHeader && isHeaderVisible) {
              return (
                <React.Fragment key={section.sectionId}>
                  <h2 className='dynamic-form-header'>{section.sectionName}</h2>
                </React.Fragment>
              );
            } else {
              return (
                <Section
                  allowAutoScroll={allowAutoScroll}
                  componentID={componentID}
                  displayLocation={section?.displayLocation}
                  formDocuments={formData?.formDocuments}
                  isInformation={isInformation}
                  isReadOnly={isReadOnly}
                  key={section?.sectionId}
                  onChange={handleOnChange}
                  questionsById={formData?.questionsById}
                  sectionData={section}
                  stepNo={stepNo}
                />
              );
            }
          })}
          {!!footerText && (
            <div className='dynamic-form-footer'>
              <MarkdownRenderer text={footerText} />
            </div>
          )}
        </div>
      </div>
    </FormProvider>
  );
};

export default Form;
